import React, { useEffect, useMemo, useState } from 'react';

import { Callout, Intent } from '@blueprintjs/core';
import { ArrowLeft } from '@carbon/icons-react';
import { HTMLHeading } from '@varicent/components';
import { GetRowIdParams, GridApi, GridOptions, IRowNode } from 'ag-grid-community';

import IconButton from 'components/Buttons/IconButton/IconButton';

import GridV2 from 'app/components/AdvancedGrid/GridV2';

import { useData } from 'app/contexts/dataProvider';
import { useScope } from 'app/contexts/scopeProvider';

import { LookupTypeEnum } from 'app/graphql/generated/graphqlApolloTypes';
import { useUpsertLookupRow } from 'app/graphql/mutations/upsertLookupRow';
import { useGetLookupTableLazy } from 'app/graphql/queries/getLookupTable';

import useShowToast from 'app/hooks/useShowToast';

import { DataPanelViews } from 'app/models/index';

import block from 'utils/bem-css-modules';
import { formatMessage } from 'utils/messages/utils';

import { buildLookupTableColumnDef } from './buildLookupTableColumnDef';
import style from './LookupTableDetail.module.pcss';
import { getScheduleList, percentageValueFormatter, ScheduleData } from './LookupTableDetailUtil';

const b = block(style);

interface LookupTableDetailProps {
  showHeader?: boolean;
  onRowUpserted?: () => void;
}

const LookupTableDetail: React.FC<LookupTableDetailProps> = ({
  showHeader = true,
  onRowUpserted
}: LookupTableDetailProps) => {
  const { selectedTable, setIsLookupRowSubmitting, setSelectedDataView } = useData();
  const { selectedPlanningCycle } = useScope();

  const [getLookupTable] = useGetLookupTableLazy({
    fetchPolicy: 'network-only',
    onError: () => {
      showToast(formatMessage('GET_LOOKUP_TABLE_SCHDULES_ERROR'), 'danger');
    }
  });

  const [callout, setCallout] = useState<{ intent: Intent; message: string }>();
  const [gridApi, setGridApi] = useState<GridApi>(null);
  const [newScheduleName, setNewScheduleName] = useState<string | null>(null);
  const [shouldFlash, setShouldFlash] = useState<boolean>(false);
  const showToast = useShowToast();

  const loadLookupTableData = async () => {
    const result = await getLookupTable({
      variables: {
        planningCycleId: selectedPlanningCycle?.id,
        lookupInput: {
          lookupId: selectedTable?.tableId,
          lookupType: [selectedTable?.tableDataType as LookupTypeEnum]
        }
      }
    });
    const scheduleList = getScheduleList(result?.data?.getPlanningCycleSpec?.lookups[0]?.metadata?.tableData);

    return scheduleList;
  };

  const defaultCalloutMessage =
    selectedTable?.tableDataType === LookupTypeEnum.Seasonality
      ? `${formatMessage('SEASONALITY_SUM_MESSAGE')} ${formatMessage('PERCENTAGE_VALUE_MESSAGE')}`
      : formatMessage('RAMP_TABLE_MESSAGE');

  const [upsertLookupRow, { loading: upsertingLookupRow }] = useUpsertLookupRow({
    onCompleted() {
      setCallout({
        message: defaultCalloutMessage,
        intent: 'primary'
      });
      // fetch the latest data
      gridApi?.refreshServerSide({ purge: true });
      // reset top pinned row
      gridApi?.getPinnedTopRow(0)?.setData({});
      onRowUpserted?.();
    },
    onError() {
      showToast(formatMessage('UPSERT_LOOKUP_ROW_ERROR'), 'danger');
    }
  });

  useEffect(() => {
    setIsLookupRowSubmitting(upsertingLookupRow);
  }, [upsertingLookupRow]);

  const handleBackClicked = () => {
    setSelectedDataView(DataPanelViews.DATA_OVERVIEW);
  };

  const seasonalityTotal = (data: Record<string, string>) => {
    let currentTotal = 0;
    Object.entries(data).forEach((entry) => {
      if (entry[0] !== 'scheduleName') {
        currentTotal = currentTotal + +entry[1];
      }
      return currentTotal;
    });
    return currentTotal;
  };

  const seasonalityValidator = (params) => {
    const { data } = params;
    const currentTotal = seasonalityTotal(data);
    if (Math.round(currentTotal) !== 100) {
      setCallout({
        message: formatMessage('YOUR_MONTH_SEASONALITY_PLEASE_ADJUST', {
          value: percentageValueFormatter(currentTotal)
        }),
        intent: 'danger'
      });
      return false;
    }
    return true;
  };

  const getRowJson = (data: ScheduleData, columns: ScheduleData) => {
    const input = {};
    const scheduleName = data.scheduleName;
    delete data.scheduleName;
    input[scheduleName] = data;

    // Fill in the default numbers if the cell is not edited
    Object.entries(data).forEach(([key, value]) => {
      data[key] = percentageValueFormatter(value);
    });

    // using initial data to only create column defs for month that exist
    // could be 3, 6, 12 months
    Object.keys(columns).forEach((key) => {
      // if the month is not set by user, assign the default value
      if (typeof data[key] === 'undefined' && key.includes('month')) {
        data[key] = selectedTable.tableDataType === LookupTypeEnum.Seasonality ? 0 : 100;
      }
    });
    return JSON.stringify(input);
  };

  const handleAddNewRow = async (params, type: string, columns) => {
    const isValid = type === LookupTypeEnum.Seasonality ? seasonalityValidator(params) : rampValidator(params);
    if (isValid && !!params.data.scheduleName) {
      setCallout({ message: defaultCalloutMessage, intent: 'primary' });
      // Store the new schedule name. After the row is added, find the new row by the name.
      setNewScheduleName(params.data.scheduleName);
      upsertLookupRow({
        variables: {
          input: {
            lookupId: selectedTable?.tableId,
            rowJson: getRowJson(params.data, columns)
          }
        }
      });
    } else if (isValid && !params.data.scheduleName) {
      setCallout({ message: formatMessage('SCHEDULE_NAME_EMPTY_ERROR'), intent: 'danger' });
    }
  };

  const rampValidator = (params) => {
    const { data } = params;
    const { _scheduleName, ...months } = data;

    let isValid = true;

    Object.values(months).forEach((value) => {
      if (typeof value === 'number' && (value > 100 || value < 0)) {
        setCallout({ message: formatMessage('RAMP_VALUE_MESSAGE'), intent: 'danger' });
        isValid = false;
      }
    });

    return isValid;
  };

  const onDuplicateNameFound = (params) => () => {
    const data = params.data;
    const field = params.colDef.field;
    const oldValue = params.oldValue;
    const newValue = params.newValue;

    data[field] = oldValue;
    if (newValue !== '') {
      setCallout({ message: formatMessage('SCHEDULE_NAME_EXIST_ERROR'), intent: 'danger' });
    }
    return false;
  };

  useEffect(() => {
    const calloutMessage =
      selectedTable?.tableDataType === LookupTypeEnum.Seasonality
        ? `${formatMessage('SEASONALITY_SUM_MESSAGE')} ${formatMessage('PERCENTAGE_VALUE_MESSAGE')}`
        : formatMessage('RAMP_TABLE_MESSAGE');
    setCallout({
      message: calloutMessage,
      intent: 'primary'
    });
  }, [selectedTable]);

  useEffect(() => {
    if (shouldFlash) {
      gridApi?.forEachNode((node: IRowNode) => {
        if (!node.data) return;
        if (node.data.scheduleName === newScheduleName) {
          // scroll to the position where the node is on the middle of the screen
          gridApi.ensureIndexVisible(node.rowIndex, 'middle');

          gridApi.flashCells({
            rowNodes: [node],
            flashDuration: 2000,
            fadeDuration: 1000
          });
        }
      });
      setShouldFlash(false);
    }
  }, [shouldFlash]);

  const lookupGridOptions = useMemo(
    (): GridOptions => ({
      singleClickEdit: true,
      stopEditingWhenCellsLoseFocus: true,
      rowModelType: 'serverSide',
      columnDefs: [],
      getRowId: (params: GetRowIdParams) => params.data?.scheduleName,
      defaultColDef: {
        editable: (params) => params.node.isRowPinned()
      },
      suppressRowVirtualisation: true,
      onGridReady: (gridEvent) => {
        setGridApi(gridEvent.api);
      },
      serverSideDatasource: {
        getRows: async (params) => {
          const data = await loadLookupTableData();
          params.success({ rowData: data, rowCount: data?.length });
          if (params.api.getColumnDefs()?.length === 0) {
            params.api.updateGridOptions({
              columnDefs: buildLookupTableColumnDef({
                tableType: selectedTable?.tableDataType,
                data,
                onDuplicateNameFound,
                handleAddNewRow
              })
            });
          } else {
            setShouldFlash(true);
          }
        }
      },
      pinnedTopRowData: [{}]
    }),
    [selectedTable]
  );

  return (
    <div className={b()} data-testid="lookup-table-detail">
      {showHeader && (
        <div className={b('header')} data-testid="lookup-table-detail-header">
          <div className={b('leftActionItems')}>
            <IconButton
              className={b('backIcon')}
              type="button"
              icon={<ArrowLeft />}
              onClick={handleBackClicked}
              testId="lookup-table-detail-header-back-icon"
            />
            <span className={b('tableTitle')}>
              <HTMLHeading tagLevel={'h5'} text={selectedTable?.tableName} data-testid="lookup-table-title" />
            </span>
          </div>
        </div>
      )}
      <div className={b('tableContainer')}>
        {showHeader && (
          <div className={b('fileType')}>
            <span data-testid="table-type-label">{formatMessage('DATA_TABLE_TYPE_WITH_COLON')}</span>
            <span className={b('fileTypeText')} data-testid="table-type">
              {selectedTable?.tableDataType}
            </span>
          </div>
        )}
        {callout && (
          <Callout intent={callout.intent} className={b('calloutDanger')}>
            <p data-testid="lookup-table-callout-message" className={b('calloutMessage')}>
              {callout.message}
            </p>
          </Callout>
        )}
        <div className={b('grid')}>
          {selectedTable?.tableDataType && <GridV2 {...lookupGridOptions} data-testid="lookup-tables-grid" />}
        </div>
      </div>
    </div>
  );
};

export default LookupTableDetail;
